import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { isApiError } from "@bw/shared/api";
import { usePairingsMine } from "../hooks/usePairingsMine";
import { useRespondPairing } from "../hooks/useRespondPairing";

/**
 * PairingInbox (SLICE 1.4-d) — respond to partner pairings for a draw. Reads the
 * caller's pairings (usePairingsMine) and lets the LINKED PARTNER Accept/Decline
 * an incoming proposal. THIN presentation: the server owns the viewer_role + the
 * fold; this only renders the role-driven affordance and posts the consent.
 *
 * ★ THE invariant of the whole "both enter, then pair" design: ACCEPT IS
 * CONSENT-ONLY — it renders NO payment surface (no card picker, no add-card form,
 * no $/fee) and posts exactly { accept: true }. Both players already paid their
 * per-player share at solo entry, so a payment step here would be the #1418
 * double-charge. (The respond mutation forwards only { accept }.)
 *
 *   - INCOMING (viewer_role "partner", status "proposed") → "{proposer} wants to
 *     pair with you" + Accept + Decline (size sm). A per-pairing latch disables
 *     both + aria-busy while THAT pairing's respond is in flight, so a
 *     double-click fires respondPairing EXACTLY once.
 *   - OUTGOING (viewer_role "proposer", status "proposed") → a quiet "Waiting for
 *     {partner}", NO Accept/Decline (only the partner consents).
 *   - Honest states: a real loading affordance, the truthful "No partner requests
 *     yet" empty copy (no fabricated requests), and an honest respond error
 *     (role=alert) — never a silent no-op / false "paired" success.
 *
 * Shows DISPLAY NAMES only (the backend hydrates them) — never a raw
 * pairing_id/draw_id as visible text (the ids ride the respond handler / React
 * key only).
 */
export function PairingInbox({
  drawId,
  tournamentId,
}: {
  drawId: string;
  tournamentId: string;
}) {
  const pairingsQ = usePairingsMine(drawId);
  const respond = useRespondPairing(drawId, tournamentId);

  // The pairing currently being responded to — drives the per-pairing latch so
  // only the acted-on proposal shows the busy/disabled state.
  const [respondingPairingId, setRespondingPairingId] = useState<string | null>(
    null,
  );

  // Clear the latch once a respond settles (so the inbox can re-enable / refetch).
  useEffect(() => {
    if (!respond.isPending) setRespondingPairingId(null);
  }, [respond.isPending]);

  function handleRespond(pairingId: string, accept: boolean) {
    // The latch: while a respond is in flight a second click is a no-op, so
    // respondPairing fires EXACTLY once.
    if (respond.isPending) return;
    setRespondingPairingId(pairingId);
    respond.mutate({ pairingId, accept });
  }

  const pairings = pairingsQ.data ?? [];
  // Only PROPOSED pairings are inbox-actionable; an accepted/declined/cancelled
  // pairing is terminal (the fold/row already reflects it elsewhere).
  const incoming = pairings.filter(
    (p) => p.viewer_role === "partner" && p.status === "proposed",
  );
  const outgoing = pairings.filter(
    (p) => p.viewer_role === "proposer" && p.status === "proposed",
  );
  const hasAny = incoming.length > 0 || outgoing.length > 0;

  return (
    <Box component="section" aria-label="Partner requests">
      {pairingsQ.isLoading ? (
        <Typography color="text.secondary" role="status" aria-live="polite">
          Checking partner requests…
        </Typography>
      ) : pairingsQ.isError ? (
        <Alert severity="error" role="alert">
          We couldn&apos;t load your partner requests. Please try again.
        </Alert>
      ) : !hasAny ? (
        <Typography color="text.secondary">No partner requests yet.</Typography>
      ) : (
        <List disablePadding>
          {incoming.map((p) => {
            const isResponding =
              respond.isPending && respondingPairingId === p.pairing_id;
            return (
              <ListItem
                key={p.pairing_id}
                disableGutters
                divider
                secondaryAction={
                  <Stack direction="row" spacing={1}>
                    <Button
                      type="button"
                      size="small"
                      variant="contained"
                      onClick={() => handleRespond(p.pairing_id, true)}
                      disabled={respond.isPending}
                      aria-busy={isResponding || undefined}
                    >
                      {isResponding ? "Accepting…" : "Accept"}
                    </Button>
                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      onClick={() => handleRespond(p.pairing_id, false)}
                      disabled={respond.isPending}
                      aria-busy={isResponding || undefined}
                    >
                      Decline
                    </Button>
                  </Stack>
                }
              >
                {/* Identity column: proposer name (primary) over a subtle
                    "wants to pair" caption — the dense .player-row vocabulary. */}
                <ListItemText
                  primary={p.proposer_display_name ?? "Someone"}
                  secondary="wants to pair"
                  slotProps={{ primary: { sx: { fontWeight: 800 } } }}
                />
              </ListItem>
            );
          })}

          {outgoing.map((p) => (
            <ListItem key={p.pairing_id} disableGutters divider>
              <ListItemText
                primary={`Waiting for ${p.partner_display_name ?? "your partner"}`}
                slotProps={{ primary: { color: "text.secondary" } }}
              />
            </ListItem>
          ))}
        </List>
      )}

      {/* ONE respond-error surface (not per-pairing). A rejected respond (the
          slot was taken, the proposal expired, or the mixed 1M+1F re-check failed
          at accept) is told to the player HONESTLY — never swallowed into a
          button that silently re-enables, and never a fabricated "paired"
          confirmation. */}
      {respond.isError ? (
        <Alert severity="error" role="alert" sx={{ mt: 2 }}>
          {mapRespondError(respond.error)}
        </Alert>
      ) : null}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Respond error → honest, actionable player copy, branched on HTTP STATUS
// (mirrors the finder's mapProposeError shape).
// ---------------------------------------------------------------------------

function mapRespondError(error: unknown): string {
  if (!isApiError(error)) {
    return "We couldn't respond to that request. Please try again.";
  }
  // 409 (the pairings unique index / a concurrent fold took the slot) or 410
  // (the proposal was withdrawn/cancelled) → the request is no longer valid.
  if (error.status === 409 || error.status === 410) {
    return "That request is no longer available.";
  }
  return "We couldn't respond to that request. Please try again.";
}
